import { AlertStatus } from '@repo/shared/dtos/alert'
import { CaseWorkerRole, CaseWorkerStatus } from '@repo/shared/dtos/caseWorker'
import { DocumentType } from '@repo/shared/dtos/document'
import { ChatMessageSender } from '@repo/shared/dtos/message'
import { LifeCycleStage, OnboardingStep, PromptTemplateType } from '@repo/shared/dtos/organization'
import { ReportTemplateType } from '@repo/shared/dtos/report'
import { Language } from '@repo/ui/lang'

import { Feedback, PromptTemplate, Resource } from '@/store/admin'
import { Alert } from '@/store/alert'
import { Beneficiary } from '@/store/beneficiary'
import { ChatMessage, ChatSession } from '@/store/chat'
import { Note } from '@/store/document'
import { ReportTemplate } from '@/store/report-templates/types'
import { SudoContextOrganization, SudoContextUser } from '@/store/sudo'
import { Task } from '@/store/task'
import { CaseWorker, Organization, User } from '@/store/user'

// Define default IDs and common values for cross-entity consistency
const defaultIds = {
  alert: 'alert1',
  beneficiary: 'benef1',
  caseWorker: 'cw1',
  chatMessage: 'msg1',
  chatSession: 'cs1',
  feedback: 'feedback1',
  note: 'note1',
  organization: 'org1',
  prompt: 'prompt1',
  promptTemplate: 'pt1',
  reportTemplate: 'rt1',
  resource: 'res1',
  task: 'task1',
  user: 'user1',
}

export const defaultTimestamp = new Date().getTime()

// Enhanced builder factory function that automatically creates consistent string values
function createBuilder<T extends { id: string }>(defaultValues: T) {
  const excludedKeys = ['id', 'status', 'role']

  // Helper function to update string fields based on a new ID
  const updateStringFieldsWithId = (item: T, newId: string) =>
    Object.entries(item).forEach(([key, value]) => {
      if (
        typeof value === 'string' &&
        !excludedKeys.includes(key) &&
        !key.endsWith('Id') &&
        value.startsWith(`${key}-`)
      ) {
        item[key as keyof T] = `${key}-${newId}` as T[keyof T]
      } else {
        item[key as keyof T] = value as T[keyof T]
      }
    })

  // Process defaultValues to add consistent string naming with IDs
  const processedDefaults = { ...defaultValues }

  // Apply naming convention to all string properties except excluded ones and properties ending with 'Id'
  updateStringFieldsWithId(processedDefaults, defaultValues.id)

  type Overrides = Partial<T>
  type This = {
    build: (overrides: Overrides) => T
    buildMany: (overridesArray: (Overrides & { id: string })[]) => T[]
    buildX: (count: number) => T[]
  }

  return {
    build: (overrides: Overrides = {}): T => {
      // Create base object from defaults
      const result = { ...processedDefaults }

      // Apply overrides, ensure ID is set first if provided
      if (overrides.id) {
        result.id = overrides.id
      }

      // Apply other overrides, auto-generating string values if needed
      updateStringFieldsWithId(result, result.id)

      return result
    },
    buildMany: function (this: This, overridesArray: (Overrides & { id: string })[] = []): T[] {
      if (overridesArray.length === 0) {
        return [{ ...processedDefaults }]
      }

      return overridesArray.map((overrides) => {
        // Start with default values
        const newItem = { ...processedDefaults }

        // Set the new ID first
        newItem.id = overrides.id

        // Update all string fields that follow our pattern
        updateStringFieldsWithId(newItem, overrides.id)

        // Apply any remaining overrides
        Object.entries(overrides).forEach(([key, value]) => {
          if (key !== 'id') {
            // Skip id as we already set it
            newItem[key as keyof T] = value as T[keyof T]
          }
        })

        return newItem
      })
    },
    buildX: function (this: This, count: number): T[] {
      const results: T[] = []

      for (let i = 0; i < count; i++) {
        // Extract the prefix (entity type) from the ID
        const baseId = processedDefaults.id
        const prefix = baseId.replace(/\d+$/, '')
        const newId = `${prefix}${i + 2}` // +2 to start from 2

        // Create a new item with the new ID
        const newItem = { ...processedDefaults, id: newId }

        // Update any string fields that follow our pattern
        updateStringFieldsWithId(newItem, newId)

        results.push(newItem as T)
      }

      return results
    },
  }
}

// Create builders with interconnected default values
const organizationBuilder = createBuilder<Organization>({
  id: defaultIds.organization,
  language: Language.EN,
  name: 'name-' + defaultIds.organization,
  description: 'description-' + defaultIds.organization,
  featureFlags: [],
  methodology: 'methodology-' + defaultIds.organization,
  url: 'url-' + defaultIds.organization,
  onboarding: { step: OnboardingStep.COMPLETED },
  chatAssistantName: '',
  chatAnswerGoal: '',
  chatMessageInstructions: '',
  chatSafetyRules: '',
  categoriesSettings: [],
  commonSources: {},
  structuredDataSchema: [],
  structuredDataDisplay: [],
  lifeCycleStage: LifeCycleStage.ONBOARDING,
})

const reportTemplateBuilder = createBuilder<ReportTemplate>({
  createdAt: defaultTimestamp,
  id: defaultIds.reportTemplate,
  instructions: 'instructions-' + defaultIds.reportTemplate,
  name: 'name-' + defaultIds.reportTemplate,
  type: ReportTemplateType.BENEFICIARY_REPORT,
  updatedAt: defaultTimestamp,
})

const sudoOrganizationBuilder = createBuilder<SudoContextOrganization>({
  id: defaultIds.organization,
  name: 'name-' + defaultIds.organization,
  featureFlags: [],
  lifeCycleStage: LifeCycleStage.ONBOARDING,
})

const caseWorkerBuilder = createBuilder<CaseWorker>({
  email: 'email-' + defaultIds.caseWorker,
  firstName: 'firstName-' + defaultIds.caseWorker,
  id: defaultIds.caseWorker,
  lastName: 'lastName-' + defaultIds.caseWorker,
  role: CaseWorkerRole.CaseWorker,
  status: CaseWorkerStatus.Enabled,
})

const userBuilder = createBuilder<User>({
  email: 'email-' + defaultIds.user,
  firstName: 'firstName-' + defaultIds.user,
  id: defaultIds.user,
  language: undefined,
  lastName: 'lastName-' + defaultIds.user,
  superAdmin: false,
})

const sudoUserBuilder = createBuilder<SudoContextUser>({
  disabled: false,
  email: 'email-' + defaultIds.user,
  firstName: 'firstName-' + defaultIds.user,
  id: defaultIds.user,
  lastName: 'lastName-' + defaultIds.user,
  organizationIds: [defaultIds.organization],
  superAdmin: false,
})

const beneficiaryBuilder = createBuilder<Beneficiary>({
  caseWorkerIds: [defaultIds.caseWorker],
  createdAt: defaultTimestamp,
  fullName: 'fullName-' + defaultIds.beneficiary,
  id: defaultIds.beneficiary,
  profile: { value: 'profile-' + defaultIds.beneficiary },
  status: 'enabled',
  updatedAt: defaultTimestamp,
  structuredData: { value: [] },
})

const chatSessionBuilder = createBuilder<ChatSession>({
  id: defaultIds.chatSession,
  startedAt: defaultTimestamp,
})

const chatMessageBuilder = createBuilder<ChatMessage>({
  content: 'content-' + defaultIds.chatMessage,
  sentAt: defaultTimestamp,
  id: defaultIds.chatMessage,
  sender: ChatMessageSender.Bot,
})

const taskBuilder = createBuilder<Task>({
  beneficiaryId: defaultIds.beneficiary,
  content: 'content-' + defaultIds.task,
  createdAt: defaultTimestamp,
  id: defaultIds.task,
  title: 'title-' + defaultIds.task,
  updatedAt: defaultTimestamp,
})

const noteBuilder = createBuilder<Note>({
  id: defaultIds.note,
  content: 'content-' + defaultIds.note,
  createdAt: defaultTimestamp,
  type: DocumentType.NOTE,
  updatedAt: defaultTimestamp,
})

const alertBuilder = createBuilder<Alert>({
  id: defaultIds.alert,
  createdAt: defaultTimestamp,
  statusChangedAt: defaultTimestamp,
  title: 'title-' + defaultIds.alert,
  content: 'content-' + defaultIds.alert,
  status: AlertStatus.UNREAD,
  beneficiaryId: defaultIds.beneficiary,
  caseWorkerId: defaultIds.caseWorker,
  chatSessionId: defaultIds.chatSession,
})

const promptTemplateBuilder = createBuilder<PromptTemplate>({
  id: defaultIds.promptTemplate,
  promptTemplateType: PromptTemplateType.CHAT_WELCOME,
  revision: 1,
  revisionDate: defaultTimestamp,
  promptText: 'promptText-' + defaultIds.promptTemplate,
  keywords: [],
})

const resourceBuilder = createBuilder<Resource>({
  content: 'content-' + defaultIds.resource,
  title: 'title-' + defaultIds.resource,
  id: defaultIds.resource,
  createdAt: defaultTimestamp,
  updatedAt: defaultTimestamp,
})

const feedbackBuilder = createBuilder<Feedback>({
  id: defaultIds.feedback,
  content: 'content-' + defaultIds.feedback,
  createdAt: defaultTimestamp,
  chatSessionId: 'sessionid1',
  beneficiaryId: 'benef1',
  message: {
    id: 'msgid1',
    content: 'message content',
    sentAt: defaultTimestamp,
    sender: ChatMessageSender.Bot,
  },
})

// Export all builders as a single object with a consistent structure
export const mockBuilders = {
  alert: alertBuilder,
  beneficiary: beneficiaryBuilder,
  caseWorker: caseWorkerBuilder,
  chatMessage: chatMessageBuilder,
  chatSession: chatSessionBuilder,
  feedback: feedbackBuilder,
  note: noteBuilder,
  organization: organizationBuilder,
  promptTemplate: promptTemplateBuilder,
  reportTemplate: reportTemplateBuilder,
  resource: resourceBuilder,
  sudoOrganizationBuilder,
  sudoUserBuilder,
  task: taskBuilder,
  user: userBuilder,
}

// Export default IDs for external reference if needed
export { defaultIds }
